import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../../services/firebase';
import { applyActionCode, confirmPasswordReset } from 'firebase/auth';
import { errorHandler } from '../../utils/errorHandler';
import { FaCheckCircle, FaTimesCircle, FaLock, FaEnvelope, FaSync } from 'react-icons/fa';
import './Auth.css';

function AuthActionHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your request...');
  const [countdown, setCountdown] = useState(5);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');

  const mode = searchParams.get('mode');
  const actionCode = searchParams.get('oobCode');

  useEffect(() => {
    const handleAuthAction = async () => {
      if (!actionCode) {
        setStatus('error');
        setMessage('Invalid action link. No action code found.');
        return;
      }

      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, actionCode);
          setStatus('success');
          setMessage('Your email address has been verified successfully! You can now sign in to your account.');
        } else if (mode === 'resetPassword') {
          setStatus('resetPassword');
          setMessage('Please enter your new password.');
        } else {
          setStatus('error');
          setMessage(`Invalid action type: ${mode}. Please request a new link.`);
        }
      } catch (error) {
        
        // More specific handling for invalid action code
        if (error.code === 'auth/invalid-action-code') {
          setStatus('invalid-code');
          setMessage('This verification link has expired or already been used. Please request a new verification email.');
        } else {
          const friendlyError = errorHandler.processError(error);
          setStatus('error');
          setMessage(`Error: ${friendlyError.message}. Please request a new link.`);
        }
      }
    };

    handleAuthAction();
  }, [actionCode, mode]);

  // Countdown and redirect when status is 'success'
  useEffect(() => {
    if (status === 'success') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/sign-in');
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [status, navigate]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      return;
    }

    if (!actionCode) {
      setMessage('Missing or invalid password reset code.');
      return;
    }

    setIsLoading(true);
    try {
      await confirmPasswordReset(auth, actionCode, newPassword);
      setStatus('success');
      setMessage('Your password has been reset successfully! You can now sign in with your new password.');
    } catch (error) {
      const friendlyError = errorHandler.processError(error);
      setStatus('error');
      setMessage(`Password reset failed: ${friendlyError.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestNewVerification = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage('Please enter your email address.');
      return;
    }
    
    setIsLoading(true);
    try {
      // Get the user from Firebase Auth
      const signInMethods = await auth.fetchSignInMethodsForEmail(email);
      
      if (signInMethods.length === 0) {
        throw new Error('No account exists with this email address.');
      }
      
      // For security reasons, we'll just tell the user we sent the email
      // even if they're not actually registered
      setMessage(`If an account exists with ${email}, a new verification email has been sent. Please check your inbox and spam folder.`);
      
      // Try to sign in temporarily to send verification email
      try {
        // This is a bit of a hack - we'll create a temporary auth session just to send the verification
        // In a production app, you might want a server-side API to handle this
        await auth.sendPasswordResetEmail(email);
        setStatus('email-sent');
      } catch (innerError) {
        // Don't expose this error to the user, but log it
      }
    } catch (error) {
      setMessage(`If an account exists with ${email}, a new verification email has been sent. Please check your inbox and spam folder.`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="auth-message">
            <div className="loading-spinner"></div>
            <p>{message}</p>
          </div>
        );

      case 'resetPassword':
        return (
          <form onSubmit={handlePasswordReset}>
            <div className="auth-icon">
              <FaLock size={40} color="var(--primary)" />
            </div>
            <h2>Reset Your Password</h2>
            {message && <p className="auth-message">{message}</p>}
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Enter your new password"
              />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Confirm your new password"
              />
            </div>
            <button 
              type="submit" 
              className="auth-button"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        );

      case 'invalid-code':
        return (
          <form onSubmit={handleRequestNewVerification}>
            <div className="auth-message error">
              <div className="error-icon">
                <FaTimesCircle size={40} />
              </div>
              <h2>Verification Link Expired</h2>
              <p>{message}</p>
            </div>
            
            <div className="form-group">
              <label>Your Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email address"
              />
            </div>
            
            <button 
              type="submit" 
              className="auth-button"
              disabled={isLoading}
            >
              <FaSync /> {isLoading ? 'Sending...' : 'Resend Verification Email'}
            </button>
            
            <button 
              type="button"
              className="auth-button secondary"
              onClick={() => navigate('/sign-in')}
            >
              Return to Sign In
            </button>
          </form>
        );

      case 'email-sent':
        return (
          <div className="auth-message success">
            <div className="success-icon">
              <FaEnvelope size={40} />
            </div>
            <h2>Email Sent</h2>
            <p>{message}</p>
            <button 
              className="auth-button"
              onClick={() => navigate('/sign-in')}
            >
              Go to Sign In
            </button>
          </div>
        );

      case 'success':
        return (
          <div className="auth-message success">
            <div className="success-icon">
              <FaCheckCircle size={40} />
            </div>
            <h2>Success!</h2>
            <p>{message}</p>
            <p className="countdown">Redirecting in {countdown} seconds...</p>
            <button 
              className="auth-button"
              onClick={() => navigate('/sign-in')}
            >
              Go to Sign In
            </button>
          </div>
        );

      case 'error':
        return (
          <div className="auth-message error">
            <div className="error-icon">
              <FaTimesCircle size={40} />
            </div>
            <h2>Error</h2>
            <p>{message}</p>
            <button 
              className="auth-button"
              onClick={() => navigate('/sign-in')}
            >
              Return to Sign In
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        {renderContent()}
      </div>
    </div>
  );
}

export default AuthActionHandler;
